import React from 'react';
import { Stack } from '@mui/material';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import styled from '@emotion/styled';
import { CompactReplaceButton } from '../style';
import { useTranslation } from 'react-i18next';

export type SearchResult = {
  id: string; // 고유 ID (인덱스)
  snippet: string; // 검색어 앞뒤 텍스트 조각
  fullText: string; // 전체 문장
  searchWord: string; // 검색어
};

type SearchResultItemProps = {
  result: SearchResult;
  replaceText?: string; // 바꿀 단어
  onClick?: (result: SearchResult) => void;
  onReplace?: (result: SearchResult) => void;
  onHover?: (resultId: string | null) => void; // hover 시 호출
  isReplacing?: boolean; // 바꾸기 중 여부
};

/**
 * 검색 결과를 표시하는 컴포넌트
 * - 검색어가 포함된 문장 표시
 * - 최대 2줄, 초과 시 말줄임
 * - 검색어는 볼드로 표시
 * - 클릭 시 해당 위치로 스크롤
 */
export default function SearchResultItem({
  result,
  replaceText = '',
  onClick,
  onReplace,
  onHover,
  isReplacing = false,
}: SearchResultItemProps) {
  const { t } = useTranslation();
  const hasReplaceText = replaceText.trim() !== '';

  const handleClick = () => {
    onClick?.(result);
  };

  const handleReplaceClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 부모 onClick 방지
    onReplace?.(result);
  };

  const handleMouseEnter = () => {
    onHover?.(result.id);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  // 검색어를 스타일링하는 함수
  const renderSnippetWithStyle = (
    snippet: string,
    searchWord: string,
    replaceText: string,
  ) => {
    if (!searchWord) return snippet;

    const parts = snippet.split(new RegExp(`(${searchWord})`, 'gi'));
    return (
      <>
        {parts.map((part, index) => {
          if (part.toLowerCase() === searchWord.toLowerCase()) {
            return (
              <React.Fragment key={index}>
                <SearchWordSpan
                  hasReplaceText={hasReplaceText}
                  className="search-word"
                >
                  {part}
                </SearchWordSpan>
                {hasReplaceText && (
                  <>
                    {' '}
                    <ReplaceWordSpan>{replaceText}</ReplaceWordSpan>
                  </>
                )}
              </React.Fragment>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  return (
    <StyledSearchResultItem
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Body1
        sx={{
          flex: 1,
          fontSize: '14px',
          fontWeight: 400,
          color: 'var(--dk-text-secondary, #888D96)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: '20px',
        }}
      >
        {renderSnippetWithStyle(result.snippet, result.searchWord, replaceText)}
      </Body1>

      {hasReplaceText && (
        <CompactReplaceButton
          variant="secondary"
          size="small"
          onClick={handleReplaceClick}
          disabled={isReplacing}
        >
          {t('WordReplace.바꾸기')}
        </CompactReplaceButton>
      )}
    </StyledSearchResultItem>
  );
}

const StyledSearchResultItem = styled(Stack)`
  padding: 8px;

  cursor: pointer;
  gap: 12px;
  border-radius: 4px;

  backdrop-filter: blur(8px);
  border-right: 4px;

  &:hover {
    background: var(--dk-purple-soft, #fbf4fa);
  }
`;

const SearchWordSpan = styled.span<{ hasReplaceText: boolean }>`
  font-weight: ${({ hasReplaceText }) => (hasReplaceText ? 400 : 700)};
  color: ${({ hasReplaceText }) => (hasReplaceText ? '#C77AC4' : 'var(--dk-text, #000)')};
  text-decoration: ${({ hasReplaceText }) =>
    hasReplaceText ? 'line-through' : 'none'};
`;

const ReplaceWordSpan = styled.span`
  font-weight: 700;
  color: #c77ac4;
`;
